<template>
  <div class="oauth-callback">
    <div class="oauth-callback__container">
      <!-- Loading state -->
      <div
        v-if="status === 'loading'"
        class="oauth-callback__content"
        data-testid="loading"
      >
        <div class="oauth-callback__spinner"></div>
        <h1 class="oauth-callback__title">Signing you in...</h1>
        <p class="oauth-callback__message">Please wait while we complete your sign-in.</p>
      </div>

      <!-- Error state -->
      <div
        v-else-if="status === 'error'"
        class="oauth-callback__content"
        data-testid="error"
      >
        <h1 class="oauth-callback__title">Sign-in Failed</h1>
        <p class="oauth-callback__message">{{ errorMessage }}</p>
        <div class="oauth-callback__actions">
          <button
            data-testid="retry-button"
            class="btn btn--primary"
            @click="processCallback"
          >
            Try Again
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { buildAuthorizeUrl, exchangeCodeForTokens } from '../../api/oauthClient';
import cognito from '../../api/cognito';
import formatOAuthTokenData from '../../utils/formatOAuthTokenData';
import setAuthorizationHeader from '../../utils/setAuthorizationHeader';
import { setTokenSource } from '../../utils/oauthTokenStorage';
import { isSafeRelativePath } from '../../utils/redirectUtils';

const DEFAULT_POST_LOGIN_PATH = '/my-account';

export default {
  name: 'OAuthCallbackPage',

  data() {
    return {
      /** 'loading' | 'error' */
      status: 'loading',
      errorMessage: '',
    };
  },

  mounted() {
    this.processCallback();
  },

  methods: {
    /**
     * Main callback handler. Supports two modes:
     *
     * 1. Auth-ui token handoff (email/password via SDK):
     *    Auth-ui redirects here with tokens in the URL hash:
     *    /oauth/callback#access_token=...&id_token=...&refresh_token=...&state=...
     *    Tokens are saved directly — no code exchange needed.
     *
     * 2. Cognito auth code flow (Google OAuth):
     *    Cognito redirects here with ?code=... after the user authenticates.
     *    PKCE verifier from sessionStorage is used to exchange the code.
     *
     * Called on mount and again when the user clicks "Try Again".
     */
    async processCallback() {
      this.status = 'loading';
      this.errorMessage = '';

      // --- Mode 1: Token handoff from auth-ui (URL hash) ---
      // Use this.$route.hash (Vue Router's parsed hash) with window.location.hash
      // as fallback. Both include the leading '#' which slice(1) removes.
      const rawHash = (this.$route.hash || window.location.hash || '').slice(1);
      const hashParams = new URLSearchParams(rawHash);
      const hashAccessToken = hashParams.get('access_token');
      const hashIdToken = hashParams.get('id_token');
      const hashRefreshToken = hashParams.get('refresh_token');
      const hashState = hashParams.get('state');

      if (hashAccessToken && hashIdToken && hashRefreshToken) {
        try {
          // Mark tokens as SDK-issued so refreshSession() uses the Cognito
          // InitiateAuth API (not the hosted UI oauth2/token endpoint).
          setTokenSource('sdk');
          await this.commitTokens(
            {
              access_token: hashAccessToken,
              id_token: hashIdToken,
              refresh_token: hashRefreshToken,
            },
            hashState,
          );
        } catch (err) {
          this.status = 'error';
          this.errorMessage =
            err && err.message
              ? `Sign-in failed: ${err.message}. Please try again.`
              : 'An unexpected error occurred. Please try again.';
        }
        return;
      }

      // --- Mode 2: Cognito authorization code flow ---
      const { code, state, error, redirect_uri_used } = this.$route.query;

      // Cognito returns error=login_required when prompt=none finds no session.
      // This is not an error — redirect to full login UI.
      if (error === 'login_required') {
        const url = await buildAuthorizeUrl({});
        window.location.href = url;
        return;
      }

      // Any other error param from Cognito — show error UI.
      if (error) {
        this.status = 'error';
        this.errorMessage = `Authentication failed: ${error}. Please try again.`;
        return;
      }

      // Missing code — should not normally happen; treat as error.
      if (!code) {
        this.status = 'error';
        this.errorMessage = 'No authorization code was received. Please try signing in again.';
        return;
      }

      try {
        // Exchange the authorization code for tokens (PKCE verifier is retrieved
        // from sessionStorage by oauthClient internally).
        // Mark as PKCE so refreshSession() uses the hosted UI oauth2/token endpoint.
        setTokenSource('pkce');
        const tokenResponse = await exchangeCodeForTokens(code, redirect_uri_used || undefined);

        // Route through auth-ui /save-session so the refresh token is stored in
        // auth-ui's localStorage. This enables prompt=none SSO for other apps
        // (e.g. testapp) after a Google login — auth-ui is bypassed on the Google
        // PKCE callback path, so it would otherwise never store the session.
        const authUiUrl = window.OAuthConfig && window.OAuthConfig.authUiUrl;
        if (authUiUrl && tokenResponse.refresh_token) {
          await this.commitTokens(tokenResponse, state);
          const destination = window.location.origin + (state && state.startsWith('/') ? state : '/my-account');
          const saveSessionParams = new URLSearchParams({
            refresh_token: tokenResponse.refresh_token,
            redirect: destination,
          });
          window.location.replace(`${authUiUrl}/save-session#${saveSessionParams.toString()}`);
          return;
        }

        await this.commitTokens(tokenResponse, state);
      } catch (err) {
        this.status = 'error';
        this.errorMessage =
          err && err.message
            ? `Sign-in failed: ${err.message}. Please try again.`
            : 'An unexpected error occurred. Please try again.';
      }
    },

    /**
     * Persist a token response, update Vuex, and redirect.
     * Shared by both the token handoff path and the code exchange path.
     */
    async commitTokens(tokenResponse, destination) {
      // Persist tokens to localStorage and set up the Cognito SDK session.
      await cognito.createUserSession(tokenResponse);

      // Decode the id_token and format into the canonical user shape.
      const user = formatOAuthTokenData({
        access_token: tokenResponse.access_token,
        id_token: tokenResponse.id_token,
        refresh_token: tokenResponse.refresh_token,
      });

      // Set the axios Authorization header for subsequent API calls.
      setAuthorizationHeader(user.idToken);

      // Commit user to Vuex store.
      this.$store.commit('user/USER_LOGGED_IN', { user, source: 'oauth' });

      // Trigger CRM sync in the background — fire and forget.
      this.$store.dispatch('user/RETRIEVE_USER_DATA');

      // Redirect to the original destination (encoded in state) or /my-account.
      //
      // Security: `destination` comes from the OAuth `state` param or the URL
      // hash — both are attacker-controllable. isSafeRelativePath() ensures only
      // relative paths within this app are accepted, preventing open redirect to
      // external domains.
      //
      // Additionally, guest-only routes are excluded so that guestRoute does not
      // immediately redirect back to myAccount (causing a NavigationRedirected
      // unhandled rejection).
      const GUEST_ONLY_PATHS = ['/', '/register', '/register/verify', '/register/verify/resend'];
      const redirectPath =
        destination && isSafeRelativePath(destination) && !GUEST_ONLY_PATHS.includes(destination)
          ? destination
          : DEFAULT_POST_LOGIN_PATH;

      this.$router.push(redirectPath).catch((err) => {
        // Swallow benign navigation failures (NavigationRedirected, NavigationDuplicated).
        // These have a numeric `type` property set by vue-router.
        if (err && err.type !== undefined) return;
        throw err;
      });
    },
  },
};
</script>
